import React, { useEffect, useRef } from 'react';
import {
  Row, Col, Space, Spin, message, useAppDispatch, useNavigate, useTranslation, roleService,
} from '@lib/index';
import { consumeExpectedState } from '@helpers/oidcLogin';
import {
  assignLoginType,
  assignRole,
  clearState,
  getRolePermissions,
} from '@features/account/AccountSlice';

const OidcConsume = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { useLoginRoleWithOidcCodeMutation } = roleService;
  const [loginRoleWithOidcCode] = useLoginRoleWithOidcCodeMutation();
  // The provider honours the code once. Without this guard a double mount
  // spends it on the first call and fails the sign-in on the second.
  const alreadyExchanged = useRef(false);

  useEffect(() => {
    if (alreadyExchanged.current) return;
    alreadyExchanged.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const expectedState = consumeExpectedState();

    // Back to the landing page, which carries the current embedded login, and
    // not to the standalone /login screen the SSO button may not have come from.
    const rejectLogin = (text: string) => {
      message.warning(text, 5);
      navigate('/');
    };

    if (!code || !state || state !== expectedState) {
      rejectLogin(t('login.ssoError'));
      return;
    }

    (async () => {
      try {
        const response = await loginRoleWithOidcCode({ code }).unwrap();
        await dispatch(assignRole(response));
        dispatch(assignLoginType('oidc'));
        await dispatch(getRolePermissions(response.token as string));
        dispatch(clearState());
        navigate('/dashboard');
      } catch (error) {
        // The API answers 404 only when the person authenticated correctly but
        // has no KSEC account, which needs a different message than a rejected
        // token: one is solved by an administrator, the other by retrying.
        const status = (error as { status?: number })?.status;
        rejectLogin(status === 404 ? t('login.ssoAccountNotRegistered') : t('login.ssoError'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Row align="middle" justify="center" className="h-screen">
      <Col xs={12} sm={12} md={12} lg={12} xl={12} className="flex justify-center">
        <Space align="center">
          <Spin size="large" tip="logging" />
        </Space>
      </Col>
    </Row>
  );
};

export default OidcConsume;
